import { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { TeamMembersList } from '../../components/teams/TeamMembersList';
import { BulkShiftTypeUpdate } from '../../components/teams/BulkShiftTypeUpdate';
import { ShiftTypeDistributionChart } from '../../components/teams/ShiftTypeDistributionChart';
import { useTeamStore } from '../../lib/store/teamStore';
import Card from '../../components/common/Card';
import Alert from '../../components/common/Alert';
import Button from '../../components/common/Button';

export const TeamShiftTypesPage = () => {
  const { teamId } = useParams<{ teamId: string }>();
  const { currentTeam, isLoading, error, setCurrentTeam, fetchTeamMembers } = useTeamStore();
  const [retryCount, setRetryCount] = useState(0);
  const [teamMembersLoaded, setTeamMembersLoaded] = useState(false);
  const [loadingMembers, setLoadingMembers] = useState(false);
  const [membersError, setMembersError] = useState<string | null>(null);
  
  // Only load the team info initially, not the members
  useEffect(() => {
    if (teamId) {
      setCurrentTeam(teamId);
    }
  }, [teamId, setCurrentTeam]);
  
  // Handle manual loading of team members
  const handleLoadMembers = async () => {
    if (!teamId) return;
    
    setLoadingMembers(true);
    setMembersError(null);
    
    try {
      await fetchTeamMembers(teamId);
      setTeamMembersLoaded(true);
    } catch (err) {
      console.error('Failed to load team members:', err);
      setMembersError(err instanceof Error ? err.message : 'Failed to fetch team members');
    } finally {
      setLoadingMembers(false);
    }
  };
  
  const handleManualRetry = () => {
    setRetryCount(0);
    handleLoadMembers();
  };
  
  // Initial loading indicator when fetching basic team info
  if (isLoading && !currentTeam && retryCount === 0) {
    return (
      <div className="flex justify-center py-12">
        <div className="animate-spin h-8 w-8 border-t-2 border-b-2 border-primary rounded-full"></div>
      </div>
    );
  }
  
  // Handle error with team loading
  if (error && !currentTeam) {
    const isResourceError = error.includes('Failed to fetch') || error.includes('insufficient resources');
    
    return (
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <Alert variant="error" className="mb-4">
          {error}
          {isResourceError && (
            <p className="mt-2 text-sm">
              The server might be experiencing high traffic. Please try again in a moment.
            </p>
          )}
        </Alert>
        
        <Button 
          variant="primary" 
          onClick={handleManualRetry}
          className="mt-2"
        >
          Try Again
        </Button>
      </div>
    );
  }
  
  if (!currentTeam) {
    return (
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <Alert variant="error" className="mb-4">
          Team not found
        </Alert>
      </div>
    );
  }
  
  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      <h1 className="text-3xl font-bold mb-6">Shift Type Management</h1>
      
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
        <Card className="p-6">
          <h2 className="text-xl font-semibold mb-2">About Shift Types</h2>
          <p className="text-gray-600">
            Shift types determine which shifts a provider can be scheduled for. The three options are:
          </p>
          <ul className="mt-2 space-y-1 list-disc list-inside text-gray-600">
            <li>
              <span className="font-medium">Days Only</span> - Provider will only be scheduled for day shifts
            </li>
            <li>
              <span className="font-medium">Nights Only</span> - Provider will only be scheduled for night shifts
            </li>
            <li>
              <span className="font-medium">Both</span> - Provider can be scheduled for both day and night shifts
            </li>
          </ul>
        </Card>
        
        {/* Only try to show distribution chart if team members are loaded */}
        {teamMembersLoaded ? (
          <ShiftTypeDistributionChart />
        ) : (
          <Card className="p-6">
            <h2 className="text-xl font-semibold mb-2">Team Member Distribution</h2>
            <p className="text-gray-600 mb-4">
              View the distribution of shift types among your team members.
            </p>
            <div className="flex justify-center">
              {loadingMembers ? (
                <div className="animate-spin h-8 w-8 border-t-2 border-b-2 border-primary rounded-full"></div>
              ) : (
                <Button
                  variant="primary"
                  onClick={handleLoadMembers}
                  disabled={loadingMembers}
                >
                  Load Team Data
                </Button>
              )}
            </div>
          </Card>
        )}
      </div>
      
      {/* Show error message if members loading failed */}
      {membersError && (
        <Alert variant="error" className="mb-6">
          {membersError}
          <p className="mt-2 text-sm">
            The server might be experiencing high traffic. Please try again in a moment.
          </p>
          <Button 
            variant="primary" 
            onClick={handleLoadMembers}
            className="mt-2"
            disabled={loadingMembers}
          >
            Try Again
          </Button>
        </Alert>
      )}
      
      {/* Only show these components if team members are loaded or explicitly requested */}
      {teamMembersLoaded ? (
        <>
          <BulkShiftTypeUpdate />
          <TeamMembersList />
        </>
      ) : (
        <Card className="p-6 mb-6">
          <h2 className="text-xl font-semibold mb-2">Team Member Management</h2>
          <p className="text-gray-600 mb-4">
            Manage shift types for your team members.
          </p>
          <div className="flex justify-center">
            {loadingMembers ? (
              <div className="animate-spin h-8 w-8 border-t-2 border-b-2 border-primary rounded-full"></div>
            ) : (
              <Button
                variant="primary"
                onClick={handleLoadMembers}
                disabled={loadingMembers}
              >
                Load Team Members
              </Button>
            )}
          </div>
        </Card>
      )}
    </div>
  );
}; 